import { useState, useEffect, useCallback, useMemo } from 'react';
import { Pagination } from 'react-bootstrap';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { Box, Paper, Typography, Tooltip, Collapse } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { getChatbotLeads, updateChatbotLeadStatus } from './FormikHelpers/Api';

const STATUS_STYLES = {
  new: { color: '#1D4ED8', background: '#EFF6FF', border: '#BFDBFE' },
  contacted: { color: '#D97706', background: '#FFFBEB', border: '#FDE68A' },
  closed: { color: '#059669', background: '#ECFDF5', border: '#6EE7B7' },
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const StatusBadge = ({ status }) => {
  const st = STATUS_STYLES[status] || STATUS_STYLES.new;
  const cls = `cb-leads__status-badge cb-leads__status-badge--${status || 'new'}`;
  return <span className={cls} style={{ color: st.color, background: st.background, border: `1px solid ${st.border}` }}>{status || 'new'}</span>;
};

/**
 * custom_fields is stored as a Mongoose Map, which serialises to a plain object
 * over JSON. However, some edge cases (older docs or direct lean() calls) may
 * return it differently. This helper always returns a safe plain-object.
 */
const normaliseCustomFields = (raw) => {
  if (!raw) return {};
  // Already a plain object (most common after JSON serialisation)
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return {};
};

const CustomFieldsDrawer = ({ lead }) => {
  const [open, setOpen] = useState(false);
  const customFields = normaliseCustomFields(lead.custom_fields);
  const entries = Object.entries(customFields).filter(([, v]) => v !== null && v !== undefined && v !== '');

  if (entries.length === 0) return <span className="cb-leads__muted" style={{ fontSize: 11 }}>—</span>;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          background: '#EFF6FF', border: '1px solid #BFDBFE',
          borderRadius: 6, padding: '2px 8px',
          fontSize: 11, fontWeight: 600, color: '#1D4ED8', cursor: 'pointer',
        }}
      >
        <FeatherIcon icon={open ? 'chevron-up' : 'chevron-down'} size={11} />
        {entries.length} field{entries.length !== 1 ? 's' : ''}
      </button>

      <Collapse in={open}>
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {entries.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 6, fontSize: 11, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, color: '#475569', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                {k.replace(/_/g, ' ')}:
              </span>
              <span style={{ color: '#0F172A' }}>{String(v)}</span>
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
};

const ChatbotLeadsTable = ({ configId, configs = [] }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [intentFilter, setIntentFilter] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const PER_PAGE = 15;

  const allIntents = useMemo(() => {
    const set = new Set(leads.map((l) => l.intent).filter(Boolean));
    return [...set];
  }, [leads]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (configId) params.config_id = configId;
      if (statusFilter) params.status = statusFilter;
      if (intentFilter) params.intent = intentFilter;
      const res = await getChatbotLeads(params);
      setLeads(res?.data || []);
      setCurrentPage(1);
    } catch {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [configId, statusFilter, intentFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleStatusChange = useCallback(async (leadId, newStatus) => {
    setUpdatingId(leadId);
    try {
      await updateChatbotLeadStatus(leadId, newStatus);
      setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, status: newStatus } : l)));
      toast.success('Lead status updated');
    } catch { /* toast handled by api layer */ }
    finally { setUpdatingId(null); }
  }, []);

  const paged = useMemo(
    () => leads.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE),
    [leads, currentPage],
  );

  const totalPages = Math.ceil(leads.length / PER_PAGE);

  const configName = (cfgId) => {
    const c = configs.find(
      (x) => x._id === cfgId || x._id?.toString() === cfgId?.toString(),
    );
    return c?.branding?.bot_name || c?.chat_slug || '—';
  };

  /* ─── header select styles (dark bg) ──────────────────────────────────── */
  const selectStyle = {
    padding: '6px 10px', borderRadius: 8,
    border: '1px solid rgba(147,197,253,0.2)',
    background: 'rgba(255,255,255,0.06)', color: '#93C5FD',
    fontSize: 12, cursor: 'pointer', outline: 'none',
  };

  return (
    <Paper elevation={0} className="ri-paper" style={{ borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: '0 4px 24px rgba(15,28,46,0.08)', overflow: 'hidden' }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="ri-header" style={{ padding: '13px 20px' }}>
        <div className="ri-header__left">
          <div className="ri-header__icon-wrap">
            <FeatherIcon icon="users" size={16} color="#93C5FD" />
          </div>
          <div className="ri-header__meta">
            <div className="ri-header__label">Chatbot</div>
            <div className="ri-header__title">Leads</div>
          </div>
          <span className="ri-header__badge ri-header__badge--records">
            {leads.length} total
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
          <select value={intentFilter} onChange={(e) => setIntentFilter(e.target.value)} style={selectStyle}>
            <option value="">All Intents</option>
            {allIntents.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <Box sx={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
        <table className="cb-leads__table">
          <thead>
            <tr>
              {['Name', 'Email', 'Phone', 'Intent', 'Form Data', 'Chatbot', 'Submitted', 'CRM', 'Status', ''].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={10}>
                  <div className="cb-leads__loading">
                    <Spinner style={{ color: '#1D4ED8' }} />
                    <span>Loading leads…</span>
                  </div>
                </td>
              </tr>
            )}

            {!loading && paged.length === 0 && (
              <tr>
                <td colSpan={10}>
                  <div className="cb-leads__empty">
                    <div className="cb-leads__empty-icon">
                      <FeatherIcon icon="inbox" size={22} color="#3B82F6" />
                    </div>
                    <Typography className="cb-leads__empty-title" style={{ fontWeight: 700, color: '#334155', fontSize: 14 }}>No Leads Yet</Typography>
                    <Typography style={{ color: '#94A3B8', fontSize: 12 }}>Leads captured through the chatbot will appear here.</Typography>
                  </div>
                </td>
              </tr>
            )}

            {!loading && paged.map((lead, idx) => (
              <tr key={lead._id} style={{ background: idx % 2 === 0 ? '#fff' : '#F8FAFC' }}>

                {/* Name */}
                <td className="cb-leads__name">
                  {lead.name || <span className="cb-leads__muted">—</span>}
                </td>

                {/* Email */}
                <td>
                  {lead.email
                    ? <a href={`mailto:${lead.email}`} className="cb-leads__email">{lead.email}</a>
                    : <span className="cb-leads__muted">—</span>}
                </td>

                {/* Phone */}
                <td style={{ whiteSpace: 'nowrap' }}>
                  {lead.phone || <span className="cb-leads__muted">—</span>}
                </td>

                {/* Intent + message preview */}
                <td>
                  <span className="cb-leads__intent-badge">{lead.intent || '—'}</span>
                  {lead.message && (
                    <div
                      style={{ fontSize: 11, color: '#64748B', marginTop: 3, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={lead.message}
                    >
                      {lead.message}
                    </div>
                  )}
                </td>

                {/* Custom fields (Map → plain object after JSON) */}
                <td style={{ minWidth: 120 }}>
                  <CustomFieldsDrawer lead={lead} />
                </td>

                {/* Chatbot name */}
                <td style={{ fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>
                  {configName(lead.chatbot_config_id)}
                </td>

                {/* Submitted — uses createdAt (timestamps: { createdAt: 'createdAt', updatedAt: false }) */}
                <td style={{ fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>
                  {fmtDate(lead.createdAt)}
                </td>

                {/* CRM status */}
                <td style={{ textAlign: 'center' }}>
                  <div className={`cb-leads__crm-icon cb-leads__crm-icon--${lead.crm_pushed ? 'pushed' : 'pending'}`}>
                    {lead.crm_pushed
                      ? <Tooltip title="Successfully pushed to CRM" arrow><span><FeatherIcon icon="check-circle" size={14} color="#059669" /></span></Tooltip>
                      : <Tooltip title={lead.crm_push_error || 'Not pushed to CRM'} arrow><span><FeatherIcon icon="minus-circle" size={14} color="#94A3B8" /></span></Tooltip>
                    }
                  </div>
                </td>

                {/* Status badge */}
                <td><StatusBadge status={lead.status} /></td>

                {/* Status change actions */}
                <td>
                  <div className="cb-leads__status-actions">
                    {['new', 'contacted', 'closed']
                      .filter((s) => s !== lead.status)
                      .map((s) => (
                        <button
                          key={s}
                          type="button"
                          disabled={updatingId === lead._id}
                          onClick={() => handleStatusChange(lead._id, s)}
                          className={`cb-leads__status-btn cb-leads__status-btn--${s}`}
                        >
                          {updatingId === lead._id ? '…' : s}
                        </button>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="ri-pagination-bar">
          <Pagination size="sm" className="ri-pg mb-0">
            <Pagination.Prev onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next onClick={() => currentPage < totalPages && setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}
    </Paper>
  );
};

export { ChatbotLeadsTable };